import * as React from "react";
import * as ReactDOM from "react-dom";

interface PopoverContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  rootRef: React.RefObject<HTMLDivElement>;
  contentRef: React.RefObject<HTMLDivElement>;
}

const PopoverContext = React.createContext<PopoverContextValue | null>(null);

interface PopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  fullWidth?: boolean;
}

export const Popover: React.FC<PopoverProps> = ({ open, onOpenChange, children, fullWidth }) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (!rootRef.current) return;
      const clickedInsideTrigger = rootRef.current.contains(target);
      const clickedInsideContent = contentRef.current?.contains(target) ?? false;
      if (open && target && !clickedInsideTrigger && !clickedInsideContent) {
        onOpenChange(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (open && e.key === 'Escape') onOpenChange(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onOpenChange]);

  const ctx: PopoverContextValue = React.useMemo(() => ({ open, setOpen: onOpenChange, rootRef, contentRef }), [open, onOpenChange]);

  return (
    <PopoverContext.Provider value={ctx}>
      <div ref={rootRef} className={fullWidth ? "relative block w-full" : "relative inline-block"}>
        {children}
      </div>
    </PopoverContext.Provider>
  );
};

interface PopoverTriggerProps {
  asChild?: boolean;
  children: React.ReactElement;
}

export const PopoverTrigger: React.FC<PopoverTriggerProps> = ({ children, asChild }) => {
  const ctx = React.useContext(PopoverContext);
  if (!ctx) return children;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    ctx.setOpen(!ctx.open);
  };

  if (asChild) {
    return React.cloneElement(children, { onClick: handleClick });
  }

  return (
    <button type="button" onClick={handleClick}>
      {children}
    </button>
  );
};

interface PopoverContentProps {
  className?: string;
  children: React.ReactNode;
}

export const PopoverContent: React.FC<PopoverContentProps> = ({ className, children }) => {
  const ctx = React.useContext(PopoverContext);
  const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null);
  const contentRef = ctx?.contentRef as React.RefObject<HTMLDivElement> | null;

  React.useEffect(() => {
    if (!ctx || !ctx.open) return;
    function calc() {
      const trigger = ctx?.rootRef.current;
      const content = contentRef?.current;
      if (!trigger) return;
      const t = trigger.getBoundingClientRect();
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;

      // Probar con dimensiones estimadas si aún no montó el contenido
      const cW = content?.offsetWidth || 320; // fallback ancho
      const cH = content?.offsetHeight || 240; // fallback alto

      const spaceBelow = viewportH - t.bottom;
      const wantBelow = spaceBelow >= cH + 12;
      const top = wantBelow ? Math.min(t.bottom + 8, viewportH - cH - 8) : Math.max(8, t.top - cH - 8);

      let left = t.left;
      if (left + cW + 8 > viewportW) left = Math.max(8, viewportW - cW - 8);
      if (left < 8) left = 8;
      setCoords({ top, left });
    }
    calc();
    const onScrollOrResize = () => calc();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [ctx?.open]);

  if (!ctx || !ctx.open) return null;
  const node = (
    <div
      ref={contentRef}
      className={`z-50 bg-white border rounded shadow-lg ${className || ""}`}
      style={{ position: 'fixed', top: coords?.top || 0, left: coords?.left || 0 }}
    >
      {children}
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
};